import { DEFAULT_OFFERS_BACKGROUND_URL } from 'constants/constants';
import { OfferType } from 'constants/enums';

import type {
    BuildCurrentPromotionParams,
    CurrentPromotion,
    DynamicOfferUi,
    FormValues,
    OfferData,
    OfferUi,
    OfferUIData,
    PlayerAvailability,
    PreviewProduct,
    PriceDiscount,
    ProductSale,
    ProductSequence,
} from '../types';

const buildOfferUi = (values: FormValues, getOfferUI: OfferUIData): OfferUi => ({
    backgroundImage: getOfferUI?.backgroundImage || values.backgroundImage || DEFAULT_OFFERS_BACKGROUND_URL,
    offerUiType: OfferType.SPECIAL_OFFER,
    specialOffer: {
        templateType: getOfferUI?.specialOffer?.templateType || 'Single',
        presentOfferEndTimer: getOfferUI?.specialOffer?.presentOfferEndTimer || false,
        title: getOfferUI?.specialOffer?.title || '',
        fontSize: getOfferUI?.specialOffer?.fontSize || 22,
        fontWeight: getOfferUI?.specialOffer?.fontWeight || 'bold',
        fontColor: getOfferUI?.specialOffer?.fontColor || { colorOne: '#87d256' },
        subTitle: {
            text: getOfferUI?.specialOffer?.subTitle?.text || '',
            fontSize: getOfferUI?.specialOffer?.subTitle?.fontSize || 16,
            fontWeight: getOfferUI?.specialOffer?.subTitle?.fontWeight || 'normal',
            fontColor: getOfferUI?.specialOffer?.subTitle?.fontColor || { colorOne: '#ffffff' },
        },
        backgroundColor: getOfferUI?.specialOffer?.backgroundColor || { colorOne: '#323281' },
    },
    borderColor: getOfferUI?.borderColor || {
        colorOne: '#ffffff',
        colorTwo: '',
        direction: '',
    },
    borderWidth: getOfferUI?.borderWidth || 2,
});

const buildDynamicOfferUi = (values: FormValues, emblemBadges: unknown[], ribbonBadges: unknown[]): DynamicOfferUi => ({
    badges: [],
    salePercentage: values.salePercentage || 0,
    salePercentageDisplayType: values.salePercentageDisplayType || 'percentage',
    offerBadges: {
        emblems: {
            data: emblemBadges || [],
        },
        ribbons: {
            data: ribbonBadges || [],
        },
    },
});

const buildProductSale = (values: FormValues): ProductSale => ({
    sale: values.salePercentage || 0,
    type: values.salePercentageDisplayType || 'percentage',
});

const buildPriceDiscount = (values: FormValues): PriceDiscount => ({
    discount: values.discount || 0,
    type: values.priceDiscountDisplayType || 'percentage',
});

const buildProductsSequence = (
    values: FormValues,
    productsListForPreview: PreviewProduct[],
    transformPriceValue: () => number
): ProductSequence[] => ([
    {
        index: 1,
        products: productsListForPreview,
        price: {
            currencyCode: 'USD',
            currencySymbol: '$',
            price: values.price || 0,
            symbolPosition: 'Left',
            spacing: false,
            fractionalSeparator: '.',
            milSeparator: ',',
            isCents: true,
            multiplier: 100,
        },
        priceInUsdCents: values.price || 0,
        prices: {
            priceInUsdCents: values.price || 0,
            priceDisplay: values.setAsFree ? '$0.00' : `$${transformPriceValue()}`,
            currencyCode: 'USD',
        },
    },
]);

const buildPlayerAvailability = (values: FormValues): PlayerAvailability | undefined =>
    values.playerAvailability ? {
        total: values.playerAvailability,
        remaining: values.playerAvailability,
    } : undefined;

export const buildCurrentPromotion = ({
    values,
    getOfferUI,
    productsListForPreview,
    badges,
    emblemBadges,
    ribbonBadges,
    transformPriceValue
}: BuildCurrentPromotionParams): CurrentPromotion => ({
    offerId: values.offerId || 'preview',
    publisherOfferId: values.publisherOfferId || '',
    offerJwt: '',
    offerType: OfferType.SPECIAL_OFFER,
    offerName: values.name || '',
    displayName: values.displayName || values.name || '',
    offerUi: buildOfferUi(values, getOfferUI),
    dynamicOfferUi: buildDynamicOfferUi(values, emblemBadges, ribbonBadges),
    badges: badges || [],
    productSale: buildProductSale(values),
    priceDiscount: buildPriceDiscount(values),
    productsSequence: buildProductsSequence(values, productsListForPreview, transformPriceValue),
    playerAvailability: buildPlayerAvailability(values),
    schedule: values.schedule,
    offerBadges: {
        emblems: emblemBadges || [],
        ribbons: ribbonBadges || [],
    },
});

export const buildOfferData = (currentPromotion: CurrentPromotion): OfferData => ({
    offers: {
        progressBars: [],
        popups: [],
        promotions: [currentPromotion],
        bundles: [],
        rolling: [],
    },
    balances: [],
    offerFocus: {},
    sections: [],
    profileFrameImg: '',
    playerLevelImg: '',
    publisherSettings: {
        popUpStartHourUTC: 0,
    },
    bannerImage: '',
    playerLevels: [],
    tabs: [],
    logo: '',
});

